import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { getMovie, paypalCaptureOrder, paypalGuestCheckout, stripeGuestCheckout } from '../services/api';
import { Movie } from '../types/api';

declare global {
  interface Window {
    paypal?: any;
  }
}

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
const PAYPAL_SDK_URL = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=AUD&intent=capture&components=buttons&disable-funding=card,credit`;
const SITE_URL = (process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://cinemazone.au')).replace(/\/+$/, '');

const loadPayPalSdk = async () => {
  if (window.paypal) return;
  if (document.querySelector('script[src^="https://www.paypal.com/sdk/js"]')) return;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PAYPAL_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
    document.body.appendChild(script);
  });
};

const stripTags = (value: string) => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const truncate = (value: string, maxLength: number) => (value.length <= maxLength ? value : `${value.slice(0, maxLength - 1).trimEnd()}...`);
const toAbsoluteUrl = (value: string) => {
  try {
    return new URL(value, `${SITE_URL}/`).toString();
  } catch {
    return value;
  }
};

export const MovieDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [error, setError] = useState('');
  const [renting, setRenting] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const paypalButtonsRef = useRef<any | null>(null);
  const paypalRenderedRef = useRef(false);
  const paypalPaymentIdRef = useRef<number | null>(null);
  const paypalAccessTokenRef = useRef<string | null>(null);
  const guestEmailRef = useRef('');

  useEffect(() => {
    getMovie(id).then((response) => setMovie(response.data)).catch(() => setError(t('movieNotFound')));
  }, [id, t]);

  useEffect(() => {
    if (!movie || searchParams.get('payment') !== '1') return;
    setPaymentDialogOpen(true);
  }, [movie, searchParams]);

  useEffect(() => {
    guestEmailRef.current = guestEmail;
  }, [guestEmail]);

  useEffect(() => {
    if (!paymentDialogOpen || !movie) return;
    let cancelled = false;
    let renderedContainer: HTMLDivElement | null = null;

    const renderButtons = async () => {
      if (!PAYPAL_CLIENT_ID) {
        setError(t('paypalNotConfigured'));
        return;
      }
      if (paypalRenderedRef.current) return;
      try {
        await loadPayPalSdk();
        for (let i = 0; i < 20 && !window.paypal; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        if (cancelled || !window.paypal || !paypalContainerRef.current) return;

        renderedContainer = paypalContainerRef.current;
        renderedContainer.innerHTML = '';
        const buttons = window.paypal.Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
          createOrder: async () => {
            setError('');
            const email = guestEmailRef.current.trim();
            if (!email) {
              setError(t('guestEmailRequired'));
              throw new Error('Guest email is required');
            }
            const response = await paypalGuestCheckout(movie.id, email);
            paypalPaymentIdRef.current = response.paymentId;
            paypalAccessTokenRef.current = response.accessToken || null;
            return response.paypalOrderId;
          },
          onApprove: async (data: any) => {
            setRenting(true);
            try {
              const capture = await paypalCaptureOrder(data.orderID, paypalAccessTokenRef.current || undefined);
              if (capture.status === 'COMPLETED' && paypalPaymentIdRef.current && paypalAccessTokenRef.current) {
                navigate(`/guest-watch/${encodeURIComponent(paypalAccessTokenRef.current)}`);
                return;
              }
              setError(t('paypalNotCompleted'));
            } catch {
              setError(t('paypalConfirmError'));
            } finally {
              setRenting(false);
            }
          },
          onCancel: () => setError(t('paymentCancelled')),
          onError: () => setError(t('paypalFailed')),
        });

        if (typeof buttons.isEligible === 'function' && !buttons.isEligible()) {
          setError(t('paypalUnavailable'));
          return;
        }

        paypalButtonsRef.current = buttons;
        await buttons.render(renderedContainer);
        if (!cancelled) paypalRenderedRef.current = true;
      } catch {
        setError(t('paypalLoadError'));
      }
    };

    renderButtons();

    return () => {
      cancelled = true;
      if (paypalButtonsRef.current && typeof paypalButtonsRef.current.close === 'function') {
        try {
          paypalButtonsRef.current.close();
        } catch {
          // Ignore PayPal SDK cleanup failures.
        }
      }
      paypalButtonsRef.current = null;
      paypalRenderedRef.current = false;
      if (renderedContainer) {
        renderedContainer.innerHTML = '';
      }
    };
  }, [movie, navigate, paymentDialogOpen, t]);

  const openRentFlow = () => {
    setError('');
    setPaymentDialogOpen(true);
  };

  const rent = async () => {
    if (!movie) return;
    if (!guestEmail.trim()) {
      setError(t('guestEmailRequired'));
      return;
    }
    setRenting(true);
    setError('');
    try {
      const checkout = await stripeGuestCheckout(movie.id, guestEmail.trim());
      window.location.href = checkout.url;
    } catch {
      setError(t('paymentStartError'));
    } finally {
      setRenting(false);
    }
  };

  if (error && !movie) {
    return <main className="mx-auto max-w-4xl px-4 py-10 text-red-700">{error}</main>;
  }

  if (!movie) {
    return <main className="mx-auto max-w-4xl px-4 py-10 text-stone-300">{t('loadingMovie')}</main>;
  }

  const actors = movie.actors?.trim();
  const releaseYear = movie.releaseYear || movie.releasedYear;
  const metaTitle = stripTags(movie.title);
  const metaDescription = truncate(stripTags(movie.description), 220);
  const metaImageUrl = toAbsoluteUrl(movie.imageUrl || movie.thumbnailUrl);
  const movieUrl = `${SITE_URL}/movies/${encodeURIComponent(movie.id)}`;
  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: metaTitle,
    description: metaDescription,
    image: metaImageUrl,
    url: movieUrl,
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Helmet>
        <title>{metaTitle} | Cinema Zone</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImageUrl} />
        <meta property="og:image:secure_url" content={metaImageUrl} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={movieUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImageUrl} />
        <link rel="canonical" href={movieUrl} />
        <script type="application/ld+json">{JSON.stringify(movieSchema)}</script>
      </Helmet>
      <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
        <section>
          <div className="overflow-hidden rounded-lg border border-stone-800 bg-stone-900">
            <img src={movie.imageUrl} alt={movie.title} className="aspect-video w-full object-cover" />
          </div>
          <h1 className="mt-6 text-4xl font-bold text-white">{movie.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-stone-300">
            {releaseYear && <span>{t('releaseYear')}: {releaseYear}</span>}
            {releaseYear && movie.durationMinutes > 0 && <span aria-hidden="true" className="text-stone-500">&bull;</span>}
            {movie.durationMinutes > 0 && <span>{t('durationMinutes', { minutes: movie.durationMinutes })}</span>}
          </div>
          {actors && (
            <p className="mt-3 text-base leading-7 text-stone-300">
              <span className="font-semibold text-stone-100">{t('cast')}:</span> {actors}
            </p>
          )}
          <p className="mt-4 text-lg leading-8 text-stone-300">{movie.description}</p>
          {movie.trailerUrl && (
            <a className="mt-4 inline-block font-semibold text-white underline decoration-[#ffd460] decoration-2 underline-offset-4" href={movie.trailerUrl} target="_blank" rel="noreferrer">
              {t('watchTrailer')}
            </a>
          )}
        </section>
        <aside className="h-fit rounded-lg border border-stone-800 bg-stone-800 p-6 shadow-sm">
          <div className="text-sm font-medium text-stone-300">{t('rentalPrice')}</div>
          <div className="mt-1 text-4xl font-bold text-white">${Number(movie.price).toFixed(2)}</div>
          <p className="mt-3 text-sm text-stone-300">{t('unlimitedPlayback')}</p>
          {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <button className="mt-6 w-full rounded-md bg-[#ffd460] px-4 py-3 font-semibold text-stone-950 hover:bg-[#ffc83d]" onClick={openRentFlow}>
            {t('rentNow')}
          </button>
          <Link className="mt-4 block text-center text-sm font-semibold text-stone-300 hover:text-white" to="/">
            {t('backToCatalog')}
          </Link>
        </aside>
      </div>

      {paymentDialogOpen && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/70 px-4 py-6">
          <div className="w-full max-w-md rounded-lg border border-stone-500 bg-stone-800 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">{t('rentMovie', { title: movie.title })}</h2>
                <div className="mt-4 rounded-lg border border-stone-800 bg-stone-900 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-stone-300">{t('rentalPrice')}</div>
                  <div className="mt-1 text-3xl font-bold text-white">${Number(movie.price).toFixed(2)}</div>
                  <p className="mt-1 text-sm text-stone-300">{t('rentalPeriodFirstPlay')}</p>
                </div>
              </div>
              <button className="rounded-md px-2 py-1 text-xl leading-none text-stone-400 hover:bg-stone-800 hover:text-white" onClick={() => setPaymentDialogOpen(false)} aria-label={t('closePaymentOptions')}>
                x
              </button>
            </div>

            {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            <div className="mt-6 space-y-3">
              <label className="block text-sm font-medium text-stone-200">
                {t('email')}
                <input
                  className="mt-1 w-full rounded-md border border-stone-600 bg-stone-950 px-3 py-2 text-white outline-none focus:border-[#ffd460] focus:ring-2 focus:ring-[#ffd460]/50"
                  type="email"
                  value={guestEmail}
                  onChange={(event) => setGuestEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </label>
              <div ref={paypalContainerRef} className="min-h-12" />
              {renting && <p className="text-center text-sm text-stone-300">{t('confirmingPaypal')}</p>}
              <button
                className="flex w-full items-center justify-center rounded-md bg-[#675dff] px-4 py-3 text-base font-semibold text-white hover:bg-[#534bff] focus:outline-none focus:ring-2 focus:ring-[#675dff] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={rent}
                disabled={renting}
              >
                {renting ? (
                  t('processing')
                ) : (
                  <>
                    {t('payWith')}
                    <img src="/images/stripe_logo.png" alt="Stripe" className="ml-1.5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};
